import type { BoardDoc } from '../../doc/BoardDoc';
import {
  attachBoxOf,
  boxLocal,
  boxPoint,
  connectorBounds,
  connectorGeometry,
  DEFAULT_LABEL_SIZE,
  fractionNear,
  insideBox,
  SIDE_ANCHORS,
  toFrame,
  type AttachBox,
  type BoxLookup,
} from '../../doc/connector';
import { distance, type Point } from '../../doc/geometry';
import type { BoxAnchor, ConnectorEnd, ConnectorItem, ConnectorProps } from '../../doc/schema';
import type { Ctx } from '../actions';
import type { ConnectTarget } from '../paneStore';
import { setConnectorStyle, toolSettings, type ConnectorStyle } from '../toolSettings';

/**
 * The connector tool's side of connectors: what an end dropped at a point attaches to, turning
 * a dragged path into bend points, the tool's style as connector props and back, and creating
 * one. Geometry itself is in doc/connector.ts.
 */

/** Screen pixels within which an end snaps to a side dot (and a frame takes it near its outline). */
const DOT_REACH_PX = 14;
/** At most this many bend points come from a drawn path. */
const MAX_DRAWN_BENDS = 6;

export const boardBoxes = (board: BoardDoc): BoxLookup => (id) => attachBoxOf(board.index.get(id));

/**
 * The item a connector end dropped at a world point attaches to, topmost first: at a side dot
 * when the point is close to one, otherwise floating when it is inside the item (frames only
 * near their outline, their inside belongs to what they hold). Null: a loose end.
 */
export function attachTargetAt(board: BoardDoc, world: Point, zoom: number): ConnectTarget | null {
  const reach = DOT_REACH_PX / zoom;
  const near = board.index.query({ x: world.x - reach, y: world.y - reach, w: reach * 2, h: reach * 2 });
  for (let i = near.length - 1; i >= 0; i--) {
    const item = near[i]!;
    const box = attachBoxOf(item);
    if (!box || board.isHidden(item)) continue;
    // on a small item the dots would cover all of it: their reach shrinks with it
    const dotReach = Math.min(reach, Math.max(4 / zoom, Math.min(box.w, box.h) * 0.25));
    let dot: BoxAnchor | null = null;
    let best = dotReach;
    for (const a of SIDE_ANCHORS) {
      const d = distance(boxPoint(box, a), world);
      if (d <= best) {
        dot = a;
        best = d;
      }
    }
    if (dot) return { itemId: item.id, anchor: dot };
    if (box.frame ? nearOutline(box, world, reach) : insideBox(box, world)) return { itemId: item.id, anchor: null };
  }
  return null;
}

function nearOutline(box: AttachBox, p: Point, reach: number): boolean {
  const a = boxLocal(box, p);
  const x = Math.abs(a.x - 0.5) * box.w;
  const y = Math.abs(a.y - 0.5) * box.h;
  const outside = x > box.w / 2 + reach || y > box.h / 2 + reach;
  const deepInside = x < box.w / 2 - reach && y < box.h / 2 - reach;
  return !outside && !deepInside;
}

/** No floating loop on a single item: an end that would float on the item the other end is on stays loose. */
export function allowedTarget(target: ConnectTarget | null, other: ConnectorEnd): ConnectTarget | null {
  if (target && target.itemId === other.item && (!target.anchor || !other.anchor)) return null;
  return target;
}

/** A connector end at a world point: attached to the target (at its dot, if it has one) or loose. */
export function endAt(board: BoardDoc, target: ConnectTarget | null, world: Point): ConnectorEnd {
  if (!target) return { item: null, anchor: null, x: world.x, y: world.y };
  const box = attachBoxOf(board.index.get(target.itemId));
  const p = target.anchor && box ? boxPoint(box, target.anchor) : world;
  return { item: target.itemId, anchor: target.anchor, x: p.x, y: p.y };
}

/** Connector props in the given style. */
export function propsFromStyle(st: ConnectorStyle, start: ConnectorEnd, end: ConnectorEnd): ConnectorProps {
  return {
    kind: st.kind,
    route: st.route,
    start,
    end,
    bend: [],
    mid: 0.5,
    startCap: st.startCap,
    endCap: st.endCap,
    stroke: st.kind === 'block' ? st.blockOutline : st.lineColor,
    width: st.kind === 'block' ? st.blockWidth : st.lineWidth,
    dash: st.dash,
    fill: st.blockFill,
    label: '',
    labelAt: 0.5,
    labelSize: DEFAULT_LABEL_SIZE,
    labelColor: null,
  };
}

/** A connector's style; what it does not have (the other kind's colours and width) comes from `base`. */
export function styleOf(p: ConnectorProps, base: ConnectorStyle): ConnectorStyle {
  const block = p.kind === 'block';
  return {
    kind: p.kind,
    route: p.route,
    startCap: p.startCap,
    endCap: p.endCap,
    dash: p.dash,
    lineColor: block ? base.lineColor : p.stroke,
    lineWidth: block ? base.lineWidth : p.width,
    blockFill: block ? p.fill : base.blockFill,
    blockOutline: block ? p.stroke : base.blockOutline,
    blockWidth: block ? p.width : base.blockWidth,
  };
}

/** The props a style sets on an existing connector (its ends, bends and label stay). */
export function stylePatch(st: ConnectorStyle): Partial<ConnectorProps> {
  const p = propsFromStyle(st, NO_END, NO_END);
  return { kind: p.kind, route: p.route, startCap: p.startCap, endCap: p.endCap, stroke: p.stroke, width: p.width, dash: p.dash, fill: p.fill };
}

const NO_END: ConnectorEnd = { item: null, anchor: null, x: 0, y: 0 };

/**
 * Restyles connectors: `patch` changes each of them (the rest of their style stays), and the
 * first one's new style becomes the tool's, so the next connector drawn looks the same.
 */
export function restyleConnectors(board: BoardDoc, items: ConnectorItem[], patch: Partial<ConnectorStyle>): void {
  const first = items[0];
  if (!first) return;
  const base = toolSettings.getState().connector;
  board.updateItems(items.map((i) => [i.id, { props: stylePatch({ ...styleOf(i.props, base), ...patch }) }]));
  setConnectorStyle({ ...styleOf(first.props, base), ...patch });
}

/**
 * The props of a connector being drawn from `start` to `end` in the tool's style. A curved one
 * follows the path the pointer took: its corners (beyond a tolerance that ignores a wobbly hand)
 * become bend points.
 */
export function drawnConnectorProps(board: BoardDoc, start: ConnectorEnd, end: ConnectorEnd, trail: Point[], zoom: number): ConnectorProps {
  const props = propsFromStyle(toolSettings.getState().connector, start, end);
  if (props.route !== 'curved' || trail.length < 3) return props;
  const boxOf = boardBoxes(board);
  const geo = connectorGeometry(props, boxOf);
  // what the pointer drew inside the items it starts and ends on is not part of the path
  const boxes = [start.item ? boxOf(start.item) : null, end.item ? boxOf(end.item) : null].filter((b): b is AttachBox => !!b);
  const inner = trail.slice(1, -1).filter((q) => !boxes.some((b) => insideBox(b, q)));
  const pts = [geo.start, ...inner, geo.end];
  let tolerance = Math.max(12 / zoom, distance(geo.start, geo.end) * 0.08);
  let kept = simplify(pts, tolerance);
  while (kept.length - 2 > MAX_DRAWN_BENDS) {
    tolerance *= 1.5;
    kept = simplify(pts, tolerance);
  }
  const bends = kept.slice(1, -1);
  return bends.length ? { ...props, bend: bends.flatMap((q) => toFrame(geo.frame, q)) } : props;
}

/** Ramer-Douglas-Peucker: the points that matter to the polyline's shape at this tolerance. */
function simplify(pts: Point[], tolerance: number): Point[] {
  if (pts.length < 3) return pts;
  const keep = new Uint8Array(pts.length);
  keep[0] = 1;
  keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [i, j] = stack.pop()!;
    let worst = -1;
    let worstD = tolerance;
    for (let k = i + 1; k < j; k++) {
      const d = offLine(pts[k]!, pts[i]!, pts[j]!);
      if (d > worstD) {
        worst = k;
        worstD = d;
      }
    }
    if (worst < 0) continue;
    keep[worst] = 1;
    stack.push([i, worst], [worst, j]);
  }
  return pts.filter((_, i) => keep[i]);
}

/** Distance from p to the segment a-b. */
function offLine(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
  return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
}

const round = (v: number) => Math.round(v * 100) / 100;

/** Adds a connector and selects it. */
export function createConnector({ board, pane }: Ctx, props: ConnectorProps): string {
  const b = connectorBounds(props, connectorGeometry(props, boardBoxes(board)));
  const id = board.createItem({ type: 'connector', x: round(b.x), y: round(b.y), w: round(b.w), h: round(b.h), props });
  pane.getState().select([id]);
  return id;
}

/**
 * Opens a connector's label for typing. One without a label yet gets it on the path where it was
 * double-clicked (`world`; null: where it would sit anyway); that spot is a draft until the text
 * is committed, so cancelling leaves the connector as it was.
 */
export function editConnectorLabel({ board, pane }: Ctx, item: ConnectorItem, world: Point | null): void {
  const s = pane.getState();
  s.select([item.id]);
  if (!item.props.label && world) s.setConnectorDraft(item.id, { labelAt: fractionNear(board.index.connectorGeometry(item).path, world) });
  s.setEditing(item.id);
}

/** Writes the label typed into a connector, at the spot it was opened at; an empty one removes it. */
export function commitConnectorLabel({ board, pane }: Ctx, item: ConnectorItem, text: string): void {
  const s = pane.getState();
  const label = text.trim();
  const at = s.connectorDraft[item.id]?.labelAt;
  const patch: Partial<ConnectorProps> = {};
  if (label !== item.props.label) patch.label = label;
  if (label && at !== undefined && at !== item.props.labelAt) patch.labelAt = at;
  if (Object.keys(patch).length) board.updateItem(item.id, { props: patch });
  s.setConnectorDraft(item.id, null);
  if (s.editingId === item.id) s.setEditing(null);
}
